import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, productImage } from "../lib/api";
import { ArrowLeft, Loader2, Mail, CreditCard, AlertCircle, Info } from "lucide-react";
import PaymentSecurity from "../components/PaymentSecurity";
import { Seo } from "../components/Seo";

export default function Checkout() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/products/${productId}`)
      .then((res) => setProduct(res.data))
      .catch(() => setError("Manualen kunde inte hittas."));
  }, [productId]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handlePay = async () => {
    setError("");
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/checkout/session", {
        product_id: productId,
        email,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError("Något gick fel. Försök igen om en stund.");
      setSubmitting(false);
    }
  };

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 text-lg text-[#4A4A4A]">
        <Seo title="Kassa" path="/kassa" noindex />
        <h1 className="font-heading text-3xl font-bold text-[#1A1A1A] mb-4">Kassa</h1>
        {error || <span className="flex items-center gap-3"><Loader2 className="animate-spin" size={26} /> Laddar...</span>}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo title="Kassa" description="Slutför ditt köp tryggt via Stripe." path="/kassa" noindex />
      <Link to={product.category === "sakerhet_digital" ? "/sakerhet" : `/manual/${product.category}`} data-testid="back-product" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6">Kassa</h1>

      {/* Summary */}
      <div className="bg-white border-2 border-gray-300 rounded-xl p-6 mt-8 flex items-center gap-5">
        {productImage(product) && (
          <img src={productImage(product)} alt={product.title} className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200" />
        )}
        <div className="flex-1">
          <p className="font-heading text-2xl font-bold text-[#1A1A1A]">{product.title}</p>
          <p className="text-base text-[#4A4A4A]">{product.category === "sakerhet_digital" ? "Digital kurs · video, ljud och PDF" : "PDF-manual · laddas ner direkt"}</p>
        </div>
        <p data-testid="checkout-price" className="font-heading text-3xl font-extrabold text-[#1A1A1A]">{Math.round(product.price)} kr</p>
      </div>

      {/* Email */}
      <div className="bg-white border-2 border-gray-300 rounded-xl p-6 md:p-8 mt-6">
        <label htmlFor="email" className="text-xl font-bold mb-2 flex items-center gap-2 text-[#1A1A1A]">
          <Mail size={24} strokeWidth={2.5} /> Din e-postadress
        </label>
        <p className="text-base text-[#4A4A4A] mb-3">Hit skickar vi din manual. Du behöver inte skapa något konto.</p>
        <input
          id="email"
          type="email"
          data-testid="checkout-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="namn@exempel.se"
          className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg focus:border-[#0056D2] w-full"
        />

        {error && (
          <p data-testid="checkout-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold mt-4">
            <AlertCircle size={22} /> {error}
          </p>
        )}

        <div data-testid="withdrawal-notice" className="flex items-start gap-3 bg-blue-50 border-2 border-blue-100 rounded-lg p-4 mt-6">
          <Info size={24} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-0.5" />
          <p className="text-base text-[#1A1A1A] leading-relaxed">
            Detta är en digital vara. När du börjar ladda ner filen upphör ångerrätten –
            du kan alltså inte ångra köpet efter det. Genom att betala godkänner du detta.
            (Detta är standard enligt lag för digitala produkter.)
          </p>
        </div>

        <button
          data-testid="pay-button"
          onClick={handlePay}
          disabled={submitting}
          className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 mt-6 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="animate-spin" size={26} /> : <CreditCard size={26} strokeWidth={2.5} />}
          {submitting ? "Öppnar betalning..." : `Betala ${Math.round(product.price)} kr med kort`}
        </button>

        <PaymentSecurity align="center" className="mt-4" />

        <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed mt-4 text-center">
          Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
        </p>
      </div>
    </div>
  );
}
